import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    IconButton,
    Chip,
    useTheme,
    useMediaQuery,
} from "@mui/material";

import FilterPanelContainer from "../Filter/Filterpopup";

// assets
import drillDownArrow from "../../assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from "../../assets/images/selectedicons/Data Drill Down1.svg";
import datadrilldown from "../../assets/images/selectedicons/DataDrillDown.svg";

const DashboardHeader = ({ title, data, isfiltericon, isfilter }) => {
    /* -------------------- MUI -------------------- */
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    /* -------------------- Header State -------------------- */
    const [openFilters, setOpenFilters] = useState(false);
    const [hover, setHover] = useState(false);

    /* -------------------- Filter State -------------------- */
    const [activeFilter, setActiveFilter] = useState("All");
    const [activeIdx, setActiveIdx] = useState(0);
    const [filterLoading, setFilterLoading] = useState(false);

    /* -------------------- Filter Presets -------------------- */
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = [
            "C4",
            "C4 Performance",
            "C4 Smart Energy",
            "C4 Ultimate",
            "XTEND",
            "Bloom Sparkling Energy",
            "Bloom Pop",
        ];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = [
            "Ecommerce",
            "Convenience Chain",
            "Convenience Independent",
            "Drug",
            "Food",
            "Multi Outlet",
            "Mass",
        ];

        switch (filterName) {
            case "All":
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Nutrabolt":
                return {
                    Company: ["Nutrabolt"],
                    Category: allCategories,
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": [
                        "C4",
                        "C4 Performance",
                        "C4 Smart Energy",
                        "C4 Ultimate",
                        "XTEND",
                    ],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Bloom":
                return {
                    Company: ["Bloom"],
                    Category: ["Functional Beverage"],
                    Brand: ["Bloom"],
                    "Sub-Brand": ["Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Functional Beverage":
                return {
                    Company: allCompanies,
                    Category: ["Functional Beverage"],
                    Brand: ["C4", "Bloom"],
                    "Sub-Brand": [
                        "C4 Performance",
                        "C4 Smart Energy",
                        "C4 Ultimate",
                        "Bloom Sparkling Energy",
                        "Bloom Pop",
                    ],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            case "Active Nutrition":
                return {
                    Company: ["Nutrabolt"],
                    Category: ["Active Nutrition"],
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };

            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() =>
        getFilterPreset("All")
    );

    /* -------------------- Handlers -------------------- */
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);

        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);

            if (filter === "Custom") {
                setOpenFilters(true);
            }
        }, 800);
    };

    const handleFiltersChange = useCallback(
        (newFilters) => {
            if (activeFilter === "Custom") return;

            const presetNames = [
                "All",
                "Nutrabolt",
                "Bloom",
                "Functional Beverage",
                "Active Nutrition",
            ];

            let matchedPreset = null;

            for (const presetName of presetNames) {
                const preset = getFilterPreset(presetName);
                if (!preset) continue;

                const isMatching = Object.keys(preset).every((key) => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();

                    return (
                        presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx])
                    );
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }

            if (!matchedPreset && activeFilter !== "Custom") {
                setActiveFilter("Custom");
                setFilterPresets(null);
            }
        },
        [activeFilter, getFilterPreset]
    );

    /* ===================================================== */
    return (

        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 1200,
            }}
        >
            {/* ================= HEADER ================= */}
            <Box
                className="top-bar-custom"
                sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                    justifyContent: isMobile ? "center" : "space-between",
                    // mb: "8px",
                    gap: isMobile ? 1 : 0,
                    background: '#FFFFFF !important',
                    padding: title === 'Intelligence' ? "0px 40px 0px 20px !important" : "0px 40px 0px 40px !important",
                    height: title === 'Intelligence' ? "68px" : isMobile ? "96px" : "68px"

                }}
            >
                {/* Title + Toggle */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "32px",
                            lineHeight: title === "Intelligence" ? "38px" : "19px",
                            fontWeight: 200,
                            color: "#111111",
                        }}
                    >
                        {title}
                    </Typography>
                    {isfiltericon && <IconButton
                        onClick={handleToggleFilters}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            p: 0,
                            "&:hover": { background: "transparent", boxShadow: 'none', },
                        }}
                    >
                        <img
                            src={
                                openFilters
                                    ? drillDownArrow
                                    : hover
                                        ? datadrillhover
                                        : datadrilldown
                            }
                            alt="Toggle"
                            width="36"
                            height="36"
                        />
                    </IconButton>}

                </Box>

                {/* Category Chips */}
                {isfilter && <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        width: isMobile ? "100%" : "auto",
                        overflowX: isMobile ? "auto" : "visible",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                    }}
                >
                    {data?.map((cat) => {
                        const isActive = activeFilter === cat;
                        return (
                            <Chip
                                key={cat}
                                label={cat}
                                onClick={() => handleFilterClick(cat)}
                                className={`dashboard-chip ${isActive ? "chip-active" : "chip-inactive"
                                    }`}
                                sx={{ border: "1px solid #1672B9" }}
                            />
                        );
                    })}
                </Box>}

            </Box>

            {/* Blue underline */}
            <Box
                className={
                    title === "Retailers"
                        ? "gradient-line-Retailers"
                        : title === "Distributors"
                            ? "gradient-line-Distributors" : title === 'Commercial Insights' ? 'gradient-line-Commercial-Insights' : title === 'Intelligence' ? 'gradient-line-Intelligence'
                                : "gradient-line-dashboard"
                }
                sx={{
                    width: "calc(100% + 82px)",
                    ml: "-40px",
                    mr: "-40px",
                    height: "4px",
                    // position: "relative",
                    zIndex: 11,
                }}
            />

            {/* Filter Panel */}
            <FilterPanelContainer
                openFilters={openFilters}
                setOpenFilters={setOpenFilters}
                filterPresets={filterPresets}
                onFiltersChange={handleFiltersChange}
            />
        </Box>


    );
};

export default DashboardHeader;
